import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Spinner from 'react-bootstrap/Spinner';
import { trackEvent } from '../utils/utils';

// Full-screen, non-closable gate shown to anyone who lands on a property
// profile page via a printed QR card (URL has `?campaign=qr_code_property`).
// They tap in their mobile, we SMS them a personalised short-link back to
// the same page, and the link click resolves to a different `sms_campaign`
// so this modal never re-shows for them.
//
// Owned by PropertyProfile.js — the parent decides when to mount/unmount
// based on the campaign param + a once-per-session unlock flag.

// Derive a readable text colour for content laid on top of the agent's
// brand colour. Mirrors the same trick used in the agent app's QR PDF
// builder so the modal feels consistent with the printed card.
const hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '').padEnd(6, '0').slice(0, 6);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};
// Neutral shimmering block reused for both the full-modal loading state
// and the inline address placeholder. One source of truth for the shimmer
// keyframes (injected once on first render).
let _qrShimmerStyleInjected = false;
const ensureShimmerKeyframes = () => {
  if (_qrShimmerStyleInjected) return;
  if (typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.dataset.qrUnlockShimmer = '1';
  el.textContent = '@keyframes qr-unlock-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }';
  document.head.appendChild(el);
  _qrShimmerStyleInjected = true;
};
const SkeletonBlock = ({ style = {} }) => {
  ensureShimmerKeyframes();
  return (
    <div
      aria-hidden
      style={{
        background: 'linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)',
        backgroundSize: '200% 100%',
        animation: 'qr-unlock-shimmer 1.4s ease-in-out infinite',
        borderRadius: 6,
        ...style,
      }}
    />
  );
};

const pickContrastText = (hex, lightnessHint) => {
  if ((lightnessHint || '').toLowerCase() === 'light') return '#1a1a1a';
  if ((lightnessHint || '').toLowerCase() === 'dark') return '#ffffff';
  const { r, g, b } = hexToRgb(hex || '#2563eb');
  const lin = (c) => {
    const cc = c / 255;
    return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return lum > 0.4 ? '#1a1a1a' : '#ffffff';
};

export default function PropertyQrUnlockModal({
  address,
  imageUrl,
  propertyId,
  propertySlug,
  agentSlug,
  agencyName,
  agentName,
  agentColour,
  agentColourLightness,
  logoUrl,
  campaign,
  loading,
  onSent,
}) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  // CSS background-image fails silently on a broken URL, so we render an
  // <img> with onError and flip to the branded fallback panel when the
  // hero photo actually fails to load. Default depends on whether we even
  // had a candidate URL to try.
  const [imageBroken, setImageBroken] = useState(false);
  const heroAvailable = !!imageUrl && !imageBroken;

  const brand = agentColour || '#2563eb';
  const brandText = pickContrastText(brand, agentColourLightness);

  // Inline two-line skeleton for the address when the rest of the modal is
  // already loaded but address_text hasn't arrived yet (edge case — usually
  // the full-modal skeleton owns this).
  const AddressSkeleton = ({ colorMode = 'light' }) => {
    const base = colorMode === 'dark' ? 'rgba(255,255,255,0.22)' : '#e5e7eb';
    const high = colorMode === 'dark' ? 'rgba(255,255,255,0.42)' : '#f3f4f6';
    const barStyle = {
      height: colorMode === 'dark' ? 22 : 18,
      borderRadius: 6,
      background: `linear-gradient(90deg, ${base} 0%, ${high} 50%, ${base} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'qr-unlock-shimmer 1.4s ease-in-out infinite',
      margin: colorMode === 'dark' ? '0 auto 6px' : '0 0 6px',
    };
    ensureShimmerKeyframes();
    return (
      <div style={{ width: '100%', maxWidth: colorMode === 'dark' ? 360 : '100%' }}>
        <div style={{ ...barStyle, width: '85%' }} />
        <div style={{ ...barStyle, width: '55%' }} />
      </div>
    );
  };

  // Win the focus war on mount — page-level Bootstrap modals try to grab
  // focus themselves, so we re-claim it on a microtask after mount.
  useEffect(() => {
    const t = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  // Lock background scroll while the gate is open — otherwise the (hidden)
  // page underneath can be wheel-scrolled on desktop, which feels broken.
  // Also force html/body white: on iOS Safari the bottom URL bar overlays
  // the viewport, and the strip behind it paints the root element's
  // background (the page's off-white #f5f3f0), not the modal. Painting the
  // root white makes that strip blend into the full-screen white gate.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    document.body.style.overflow = 'hidden';
    document.body.style.background = '#fff';
    document.documentElement.style.background = '#fff';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
    };
  }, []);

  // Fire-and-forget agent notification on first paint — the agent wants to
  // know whenever someone hits this gate (separate from the SMS-send and
  // the eventual page-open notifications). No client-side dedupe: the
  // backend's send_agent_alert already caps the per-agent email at one per
  // 24h per (event_type, property_id), and the Bayan/admin BCC is sent
  // unconditionally — so leaving rate-limiting to the server.
  //
  // Module-level guard prevents double-fire from React 18 strict-mode
  // dev-only effect remounts on the same component instance.
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (loading) return; // wait for real props before alerting the agent
    if (!agentSlug || !propertyId) return;
    if (impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    axios.post(`${window.api_host}/property-qr-modal-impression`, {
      agent_slug: agentSlug,
      property_id: propertyId,
      address_text: address,
      campaign: campaign || 'qr_code_property',
    }).catch(() => { /* fire-and-forget */ });
    try {
      trackEvent('property_qr_modal_impression', {
        agent_slug: agentSlug,
        property_id: propertyId,
        property_address: address,
        campaign: campaign || 'qr_code_property',
      });
    } catch (_) { /* never block UX on tracking */ }
  }, [agentSlug, propertyId, address, campaign, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || sent) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }

    // Tolerant client-side check. Backend re-validates and normalises to
    // +614… — we only want to catch obvious typos before the round trip.
    const cleaned = mobile.replace(/[\s-]/g, '');
    const looksAu =
      /^04\d{8}$/.test(cleaned) ||
      /^\+614\d{8}$/.test(cleaned) ||
      /^614\d{8}$/.test(cleaned) ||
      /^4\d{8}$/.test(cleaned);
    if (!looksAu) {
      setError('Please enter a valid Australian mobile (starts with 04).');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${window.api_host}/send-property-sms`, {
        name: trimmedName,
        mobile: cleaned,
        property_id: propertyId,
        property_slug: propertySlug,
        agent_slug: agentSlug,
        address_text: address,
        campaign: 'qr_code_property_sms_link',
        source_tag: 'qr_modal',
      });
      if (res?.data?.success) {
        setSent(true);
        try {
          trackEvent('property_qr_modal_link_sent', {
            agent_slug: agentSlug,
            property_id: propertyId,
            property_address: address,
          });
        } catch (_) { /* never block UX on tracking */ }
        onSent && onSent();
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to send.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      // Sits ABOVE every navbar / sticky banner on the site. The agent app's
      // MainNav uses z-index in the low hundreds; 2147483600 is just under
      // the int32 max so nothing in this tree can cover us.
      // iOS Safari's URL bar and bottom toolbar overlay `100vh`, leaving a
      // strip of the page visible behind the chrome. `100dvh` (dynamic
      // viewport height) accounts for the actual visible region, and
      // `100lvh` (large viewport, always-expanded) is the safe fallback.
      // We also paint the modal background colour all the way to the
      // viewport edges (no centred max-width on the outer container) so
      // whatever does show behind the toolbar is at least the modal's
      // white background, not the property page.
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100vh',
        background: '#fff',
        zIndex: 2147483600,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      ref={(el) => {
        if (!el) return;
        // Set the dynamic units via setProperty so older Safari/SSR don't
        // choke on parsing them in the inline style object above.
        try {
          el.style.height = '100dvh';
          el.style.minHeight = '100lvh';
        } catch (_) { /* unsupported browser — fall back to 100vh */ }
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        }}
      >
        {loading ? (
          // Whole-modal skeleton: neutral grey shimmer blocks shaped like
          // the eventual layout. Avoids the brand-coloured fallback panel
          // flashing in before propertyData/agentDetails resolve.
          <>
            <SkeletonBlock style={{
              width: '100%',
              height: 'min(45vh, 360px)',
              flexShrink: 0,
              borderRadius: 0,
            }} />
            <div
              style={{
                padding: 24,
                maxWidth: 520,
                width: '100%',
                margin: '0 auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                boxSizing: 'border-box',
                gap: 10,
              }}
            >
              <SkeletonBlock style={{ height: 12, width: '55%' }} />
              <SkeletonBlock style={{ height: 22, width: '85%' }} />
              <SkeletonBlock style={{ height: 22, width: '55%', marginBottom: 12 }} />
              <SkeletonBlock style={{ height: 14, width: '30%' }} />
              <SkeletonBlock style={{ height: 46, width: '100%', borderRadius: 10 }} />
              <SkeletonBlock style={{ height: 46, width: '100%', borderRadius: 10, marginTop: 8 }} />
            </div>
          </>
        ) : heroAvailable ? (
          <div
            style={{
              width: '100%',
              height: 'min(45vh, 360px)',
              flexShrink: 0,
              overflow: 'hidden',
              background: '#f3f4f6',
            }}
          >
            <img
              src={imageUrl}
              alt={address || 'Property photo'}
              onError={() => setImageBroken(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </div>
        ) : (
          // No hero photo on file — fall back to a branded panel showing the
          // agency colour, logo (if any) and address so the page still feels
          // intentional rather than half-loaded.
          <div
            style={{
              width: '100%',
              height: 'min(45vh, 360px)',
              flexShrink: 0,
              background: brand,
              color: brandText,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: 2.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                opacity: 0.85,
                marginBottom: 10,
              }}
            >
              {agencyName || 'Your local agency'}
            </div>
            {address ? (
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  maxWidth: 420,
                }}
              >
                {address}
              </div>
            ) : (
              <AddressSkeleton colorMode="dark" />
            )}
          </div>
        )}

        {!loading && (
        <div
          style={{
            padding: 24,
            maxWidth: 520,
            width: '100%',
            margin: '0 auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: brand,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Your free property report{agencyName ? ` — ${agencyName}` : ''}
          </div>
          {/* The no-image fallback panel already shows the address in big
              type; only repeat it in the body when there IS a hero image
              (since the photo doesn't tell the recipient the address). */}
          {heroAvailable && (
            address ? (
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.25,
                  marginBottom: 14,
                }}
              >
                {address}
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <AddressSkeleton colorMode="light" />
              </div>
            )
          )}

          {sent ? (
            <>
              <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.55, margin: '0 0 14px' }}>
                Done — check your phone. We just texted you a link to your free
                report for this address.
              </p>
              <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                Tap the link in the message to open the report on this device.
              </p>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <label
                htmlFor="qr-modal-name"
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 6,
                }}
              >
                Name
              </label>
              <input
                ref={inputRef}
                id="qr-modal-name"
                type="text"
                autoComplete="name"
                autoFocus
                placeholder="Your name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError('');
                }}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: 16,
                  border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: 10,
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: '#fff',
                  marginBottom: 14,
                }}
              />
              <label
                htmlFor="qr-modal-mobile"
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 6,
                }}
              >
                Mobile number
              </label>
              <input
                id="qr-modal-mobile"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="04xx xxx xxx"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  if (error) setError('');
                }}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: 16,
                  border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: 10,
                  outline: 'none',
                  boxSizing: 'border-box',
                  background: '#fff',
                }}
              />
              {error && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#b91c1c',
                  }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !mobile || !name.trim()}
                style={{
                  marginTop: 14,
                  width: '100%',
                  padding: '13px 16px',
                  background: submitting || !mobile || !name.trim() ? '#94a3b8' : brand,
                  color: submitting || !mobile || !name.trim() ? '#fff' : brandText,
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: submitting || !mobile || !name.trim() ? 'not-allowed' : 'pointer',
                  letterSpacing: 0.2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                {submitting && (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    style={{ width: 16, height: 16, borderWidth: 2 }}
                  />
                )}
                {submitting ? 'Sending…' : 'Send me the link'}
              </button>
            </form>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
